/**
 * @birhaus/themes
 * 
 * Pre-built theme configurations implementing BIRHAUS design principles
 * for specific industries and use cases.
 * 
 * Features:
 * - Industry-specific color palettes and typography
 * - Spanish-first internationalization
 * - WCAG AA+ accessibility compliance
 * - Cognitive load optimization
 * - 8pt grid system consistency
 * - Professional design patterns
 */

// Theme configurations
export { 
  financialTheme, 
  getFinancialColor, 
  getStatusBadgeStyles, 
  getFinancialLabel,
  financialThemeCSS
} from './themes/financial'

export { healthcareTheme } from './themes/healthcare'
export { educationTheme } from './themes/education'

// Types
export type { BirhausThemeConfig } from './themes/financial'
import type { BirhausThemeConfig } from './themes/financial'

// Theme registry for dynamic theme switching
export interface BirhausThemeRegistry {
  default: typeof import('./themes/financial').financialTheme
  financial: typeof import('./themes/financial').financialTheme
  healthcare: typeof import('./themes/healthcare').healthcareTheme
  education: typeof import('./themes/education').educationTheme
  educational: typeof import('./themes/education').educationTheme // Alias for backward compatibility
}

// Import themes using ES modules
import { financialTheme } from './themes/financial'
import { healthcareTheme } from './themes/healthcare'
import { educationTheme } from './themes/education'

export const birhausThemes: BirhausThemeRegistry = {
  default: financialTheme, // Use financial as default theme
  financial: financialTheme,
  healthcare: healthcareTheme,
  education: educationTheme,
  educational: educationTheme // Alias for backward compatibility
}

// Theme utilities - Fix object injection by using explicit property access
export function getBirhausTheme(themeName: keyof BirhausThemeRegistry) {
  switch (themeName) {
    case 'financial': return birhausThemes.financial
    case 'healthcare': return birhausThemes.healthcare
    case 'education': return birhausThemes.education
    default: return birhausThemes.financial
  }
}

export function getBirhausThemeCSS(themeName: keyof BirhausThemeRegistry) {
  const theme = getBirhausTheme(themeName)
  
  return {
    '--birhaus-primary': theme.colors.primary,
    '--birhaus-primary-hover': theme.colors.primaryHover,
    '--birhaus-success': theme.colors.success,
    '--birhaus-warning': theme.colors.warning,
    '--birhaus-error': theme.colors.error,
    '--birhaus-info': theme.colors.info,
    '--birhaus-background': theme.colors.background,
    '--birhaus-surface': theme.colors.surface,
    '--birhaus-border': theme.colors.border,
    '--birhaus-text-primary': theme.colors.textPrimary,
    '--birhaus-text-secondary': theme.colors.textSecondary,
    '--birhaus-income': theme.colors.income,
    '--birhaus-expense': theme.colors.expense,
    '--birhaus-transfer': theme.colors.transfer,
    
    '--birhaus-font-sans': theme.typography.fontFamily.sans.join(', '),
    '--birhaus-font-mono': theme.typography.fontFamily.mono.join(', '),
    
    '--birhaus-radius-sm': theme.borderRadius.sm,
    '--birhaus-radius-base': theme.borderRadius.base,
    '--birhaus-radius-lg': theme.borderRadius.lg,
    
    '--birhaus-shadow-sm': theme.boxShadow.sm,
    '--birhaus-shadow-base': theme.boxShadow.base,
    '--birhaus-shadow-lg': theme.boxShadow.lg,
    
    '--birhaus-spacing-1': theme.spacing['1'],
    '--birhaus-spacing-2': theme.spacing['2'],
    '--birhaus-spacing-4': theme.spacing['4'],
    '--birhaus-spacing-6': theme.spacing['6'],
    '--birhaus-spacing-8': theme.spacing['8']
  }
}

// React hook for theme context
export function useBirhausTheme(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)
  const cssVariables = getBirhausThemeCSS(themeName)
  
  return {
    theme,
    cssVariables,
    colors: theme.colors,
    typography: theme.typography,
    spacing: theme.spacing,
    components: theme.components,
    labels: theme.labels
  }
}

// Export useTheme as alias for useBirhausThemeV3 (for v3 component compatibility)
export const useTheme = useBirhausThemeV3

// v3.0 React hook for enhanced theme context
export function useBirhausThemeV3(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)
  const cssVariables = getBirhausThemeCSSV3(themeName)
  const hasV3Features = !!(theme.glass || theme.spacingV3 || theme.borderRadiusV3 || theme.shadowV3 || theme.animationV3)
  
  return {
    theme,
    cssVariables,
    colors: theme.colors,
    typography: theme.typography,
    spacing: theme.spacing,
    spacingV3: theme.spacingV3,
    borderRadiusV3: theme.borderRadiusV3,
    shadowV3: theme.shadowV3,
    animationV3: theme.animationV3,
    glass: theme.glass,
    components: theme.components,
    labels: theme.labels,
    hasV3Features,
    
    // v3 Utility functions
    getGlassCSS: (variant: 'light' | 'medium' | 'heavy' = 'medium') => generateGlassCSS(themeName, variant),
    generateV3Utilities: () => generateCSSUtilitiesV3(themeName),
    getTailwindConfigV3: () => generateTailwindConfigV3(themeName)
  }
}

// Helper function to generate color configuration
function generateColorConfig(theme: BirhausThemeConfig) {
  return {
    primary: {
      DEFAULT: theme.colors.primary,
      hover: theme.colors.primaryHover,
      disabled: theme.colors.primaryDisabled
    },
    success: {
      DEFAULT: theme.colors.success,
      bg: theme.colors.successBackground
    },
    warning: {
      DEFAULT: theme.colors.warning,
      bg: theme.colors.warningBackground
    },
    error: {
      DEFAULT: theme.colors.error,
      bg: theme.colors.errorBackground
    },
    info: {
      DEFAULT: theme.colors.info,
      bg: theme.colors.infoBackground
    },
    surface: {
      DEFAULT: theme.colors.surface,
      elevated: theme.colors.surfaceElevated
    },
    border: {
      DEFAULT: theme.colors.border,
      subtle: theme.colors.borderSubtle
    },
    text: {
      primary: theme.colors.textPrimary,
      secondary: theme.colors.textSecondary,
      disabled: theme.colors.textDisabled,
      inverse: theme.colors.textInverse
    },
    financial: {
      income: theme.colors.income,
      expense: theme.colors.expense,
      transfer: theme.colors.transfer,
      pending: theme.colors.pending,
      approved: theme.colors.approved,
      rejected: theme.colors.rejected
    }
  }
}

// Helper function to generate typography and layout configuration
function generateTypographyAndLayoutConfig(theme: BirhausThemeConfig) {
  return {
    fontFamily: {
      sans: theme.typography.fontFamily.sans,
      mono: theme.typography.fontFamily.mono
    },
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeight,
    spacing: theme.spacing,
    borderRadius: theme.borderRadius,
    boxShadow: theme.boxShadow
  }
}

// Tailwind CSS configuration generator
export function generateTailwindConfig(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)
  const colorConfig = generateColorConfig(theme)
  const layoutConfig = generateTypographyAndLayoutConfig(theme)

  return {
    theme: {
      extend: {
        colors: colorConfig,
        ...layoutConfig
      }
    }
  }
}

// v3.0 Enhanced Tailwind Configuration Generator
export function generateTailwindConfigV3(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)
  const baseConfig = generateTailwindConfig(themeName)

  const v3Config: any = { ...baseConfig }

  // Add v3 spacing if available
  if (theme.spacingV3) {
    v3Config.theme.extend.spacing = {
      ...v3Config.theme.extend.spacing,
      'v3-xs': theme.spacingV3.xs,
      'v3-sm': theme.spacingV3.sm,
      'v3-md': theme.spacingV3.md,
      'v3-lg': theme.spacingV3.lg,
      'v3-xl': theme.spacingV3.xl,
      'v3-2xl': theme.spacingV3['2xl'],
      'v3-3xl': theme.spacingV3['3xl']
    }
  }

  // Add v3 border radius if available
  if (theme.borderRadiusV3) {
    v3Config.theme.extend.borderRadius = {
      ...v3Config.theme.extend.borderRadius,
      'v3-xs': theme.borderRadiusV3.xs,
      'v3-sm': theme.borderRadiusV3.sm,
      'v3-md': theme.borderRadiusV3.md,
      'v3-lg': theme.borderRadiusV3.lg,
      'v3-xl': theme.borderRadiusV3.xl,
      'v3-2xl': theme.borderRadiusV3['2xl'],
      'v3-3xl': theme.borderRadiusV3['3xl'],
      'v3-card': theme.borderRadiusV3.card,
      'v3-button': theme.borderRadiusV3.button,
      'v3-input': theme.borderRadiusV3.input,
      'v3-dialog': theme.borderRadiusV3.dialog
    }
  }

  // Add v3 shadows if available
  if (theme.shadowV3) {
    v3Config.theme.extend.boxShadow = {
      ...v3Config.theme.extend.boxShadow,
      'v3-sm': theme.shadowV3.sm,
      'v3-md': theme.shadowV3.md,
      'v3-lg': theme.shadowV3.lg,
      'v3-xl': theme.shadowV3.xl,
      'glass': theme.shadowV3.glass,
      'floating': theme.shadowV3.floating,
      'elevated': theme.shadowV3.elevated
    }
  }

  // Add v3 animation if available
  if (theme.animationV3) {
    v3Config.theme.extend.transitionDuration = {
      ...v3Config.theme.extend.transitionDuration,
      'v3-fast': theme.animationV3.duration.fast,
      'v3-base': theme.animationV3.duration.base,
      'v3-slow': theme.animationV3.duration.slow
    }
    
    v3Config.theme.extend.transitionTimingFunction = {
      ...v3Config.theme.extend.transitionTimingFunction,
      'v3-smooth': theme.animationV3.easing.smooth,
      'v3-snappy': theme.animationV3.easing.snappy,
      'v3-bounce': theme.animationV3.easing.bounce,
      'v3-gentle': theme.animationV3.easing.gentle
    }
  }

  // Glass morphism utilities can be generated via generateGlassCSS function
  // Tailwind plugin integration available separately

  return v3Config
}

// v3.0 Enhanced CSS Utilities Generator
export function generateCSSUtilitiesV3(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)
  const baseUtilities = generateCSSUtilities(themeName)

  let v3Utilities = ''

  // Add glass morphism utilities
  if (theme.glass) {
    v3Utilities += '\n/* v3.0 Glass Morphism Utilities */'
    v3Utilities += generateGlassCSS(themeName, 'light')
    v3Utilities += generateGlassCSS(themeName, 'medium')
    v3Utilities += generateGlassCSS(themeName, 'heavy')
  }

  // Add v3 spacing utilities
  if (theme.spacingV3) {
    v3Utilities += `
/* v3.0 Generous Spacing Utilities */
.birhaus-p-v3-xs { padding: ${theme.spacingV3.xs}; }
.birhaus-p-v3-sm { padding: ${theme.spacingV3.sm}; }
.birhaus-p-v3-md { padding: ${theme.spacingV3.md}; }
.birhaus-p-v3-lg { padding: ${theme.spacingV3.lg}; }
.birhaus-p-v3-xl { padding: ${theme.spacingV3.xl}; }
.birhaus-m-v3-xs { margin: ${theme.spacingV3.xs}; }
.birhaus-m-v3-sm { margin: ${theme.spacingV3.sm}; }
.birhaus-m-v3-md { margin: ${theme.spacingV3.md}; }
.birhaus-m-v3-lg { margin: ${theme.spacingV3.lg}; }
.birhaus-m-v3-xl { margin: ${theme.spacingV3.xl}; }
`
  }

  // Add v3 border radius utilities
  if (theme.borderRadiusV3) {
    v3Utilities += `
/* v3.0 Radical Border Radius Utilities */
.birhaus-rounded-v3-xs { border-radius: ${theme.borderRadiusV3.xs}; }
.birhaus-rounded-v3-sm { border-radius: ${theme.borderRadiusV3.sm}; }
.birhaus-rounded-v3-md { border-radius: ${theme.borderRadiusV3.md}; }
.birhaus-rounded-v3-lg { border-radius: ${theme.borderRadiusV3.lg}; }
.birhaus-rounded-v3-xl { border-radius: ${theme.borderRadiusV3.xl}; }
.birhaus-rounded-card-v3 { border-radius: ${theme.borderRadiusV3.card}; }
.birhaus-rounded-button-v3 { border-radius: ${theme.borderRadiusV3.button}; }
.birhaus-rounded-input-v3 { border-radius: ${theme.borderRadiusV3.input}; }
`
  }

  // Add v3 shadow utilities
  if (theme.shadowV3) {
    v3Utilities += `
/* v3.0 Enhanced Shadow Utilities */
.birhaus-shadow-v3-sm { box-shadow: ${theme.shadowV3.sm}; }
.birhaus-shadow-v3-md { box-shadow: ${theme.shadowV3.md}; }
.birhaus-shadow-v3-lg { box-shadow: ${theme.shadowV3.lg}; }
.birhaus-shadow-glass { box-shadow: ${theme.shadowV3.glass}; }
.birhaus-shadow-floating { box-shadow: ${theme.shadowV3.floating}; }
.birhaus-shadow-elevated { box-shadow: ${theme.shadowV3.elevated}; }
`
  }

  // Add v3 animation utilities
  if (theme.animationV3) {
    v3Utilities += `
/* v3.0 Smooth Animation Utilities */
.birhaus-transition-v3-fast { 
  transition-duration: ${theme.animationV3.duration.fast};
  transition-timing-function: ${theme.animationV3.easing.smooth};
}
.birhaus-transition-v3-base { 
  transition-duration: ${theme.animationV3.duration.base};
  transition-timing-function: ${theme.animationV3.easing.smooth};
}
.birhaus-transition-v3-slow { 
  transition-duration: ${theme.animationV3.duration.slow};
  transition-timing-function: ${theme.animationV3.easing.gentle};
}
.birhaus-bounce-v3 { 
  transition-timing-function: ${theme.animationV3.easing.bounce};
}
`
  }

  return baseUtilities + v3Utilities
}

// Helper function to generate basic utility classes
function generateBasicUtilities(theme: BirhausThemeConfig) {
  return `
.birhaus-text-primary { color: ${theme.colors.textPrimary}; }
.birhaus-text-secondary { color: ${theme.colors.textSecondary}; }
.birhaus-bg-primary { background-color: ${theme.colors.primary}; }
.birhaus-bg-surface { background-color: ${theme.colors.surface}; }
.birhaus-border-default { border-color: ${theme.colors.border}; }`
}

// Helper function to generate button utilities
function generateButtonUtilities(theme: BirhausThemeConfig) {
  return `
.birhaus-btn-primary {
  background-color: ${theme.colors.primary};
  color: ${theme.colors.textInverse};
  padding: ${theme.spacing['3']} ${theme.spacing['6']};
  border-radius: ${theme.borderRadius.base};
  font-family: ${theme.typography.fontFamily.sans.join(', ')};
  font-weight: ${theme.typography.fontWeight.medium};
  box-shadow: ${theme.boxShadow.sm};
}

.birhaus-btn-primary:hover {
  background-color: ${theme.colors.primaryHover};
  box-shadow: ${theme.boxShadow.base};
}`
}

// Helper function to generate component utilities
function generateComponentUtilities(theme: BirhausThemeConfig) {
  return `
.birhaus-card {
  background-color: ${theme.components.card.bg};
  border: 1px solid ${theme.components.card.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.components.card.padding};
  box-shadow: ${theme.components.card.shadow};
}

.birhaus-input {
  background-color: ${theme.components.form.inputBg};
  border: 1px solid ${theme.components.form.inputBorder};
  border-radius: ${theme.borderRadius.base};
  padding: ${theme.spacing['2']} ${theme.spacing['3']};
  font-family: ${theme.typography.fontFamily.sans.join(', ')};
  color: ${theme.colors.textPrimary};
}

.birhaus-input:focus {
  border-color: ${theme.components.form.inputFocusBorder};
  outline: none;
  box-shadow: 0 0 0 3px ${theme.colors.primary}33;
}`
}

// Helper function to generate status utilities
function generateStatusUtilities(theme: BirhausThemeConfig) {
  return `
.birhaus-currency-positive { color: ${theme.components.currency.positive}; }
.birhaus-currency-negative { color: ${theme.components.currency.negative}; }
.birhaus-currency-neutral { color: ${theme.components.currency.neutral}; }

.birhaus-status-pending {
  background-color: ${theme.components.statusBadge.pending.bg};
  color: ${theme.components.statusBadge.pending.text};
  border: 1px solid ${theme.components.statusBadge.pending.border};
}

.birhaus-status-approved {
  background-color: ${theme.components.statusBadge.approved.bg};
  color: ${theme.components.statusBadge.approved.text};
  border: 1px solid ${theme.components.statusBadge.approved.border};
}

.birhaus-status-rejected {
  background-color: ${theme.components.statusBadge.rejected.bg};
  color: ${theme.components.statusBadge.rejected.text};
  border: 1px solid ${theme.components.statusBadge.rejected.border};
}`
}

// CSS utilities for custom implementations
export function generateCSSUtilities(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)

  const basicUtilities = generateBasicUtilities(theme)
  const buttonUtilities = generateButtonUtilities(theme)
  const componentUtilities = generateComponentUtilities(theme)
  const statusUtilities = generateStatusUtilities(theme)

  return `/* BIRHAUS ${theme.name} Theme CSS Utilities */
${basicUtilities}
${buttonUtilities}
${componentUtilities}
${statusUtilities}
`
}

// v3.0 Glass Morphism Utilities
export function generateGlassCSS(themeName: keyof BirhausThemeRegistry = 'financial', variant: 'light' | 'medium' | 'heavy' = 'medium') {
  const theme = getBirhausTheme(themeName)
  
  if (!theme.glass) {
    return ''
  }

  const blur = theme.glass.blurIntensity[variant]
  const opacity = theme.glass.opacity[variant]
  const saturation = theme.glass.saturation

  return `
.birhaus-glass-${variant} {
  backdrop-filter: blur(${blur}px) saturate(${saturation}%);
  background: ${theme.glass.surface};
  border: 1px solid ${theme.glass.border};
  position: relative;
}

.birhaus-glass-${variant}::before {
  content: '';
  position: absolute;
  inset: 0;
  background: ${theme.glass.primary};
  opacity: ${opacity};
  border-radius: inherit;
  pointer-events: none;
}
`
}

// v3.0 Enhanced CSS Variable Generation
export function getBirhausThemeCSSV3(themeName: keyof BirhausThemeRegistry = 'financial') {
  const theme = getBirhausTheme(themeName)
  const baseCSS = getBirhausThemeCSS(themeName)

  const v3CSS: Record<string, string> = {}

  // v3 Glass properties
  if (theme.glass) {
    v3CSS['--birhaus-glass-primary'] = theme.glass.primary
    v3CSS['--birhaus-glass-secondary'] = theme.glass.secondary
    v3CSS['--birhaus-glass-surface'] = theme.glass.surface
    v3CSS['--birhaus-glass-border'] = theme.glass.border
    v3CSS['--birhaus-glass-blur-light'] = `${theme.glass.blurIntensity.light}px`
    v3CSS['--birhaus-glass-blur-medium'] = `${theme.glass.blurIntensity.medium}px`
    v3CSS['--birhaus-glass-blur-heavy'] = `${theme.glass.blurIntensity.heavy}px`
    v3CSS['--birhaus-glass-saturation'] = `${theme.glass.saturation}%`
  }

  // v3 Generous spacing
  if (theme.spacingV3) {
    v3CSS['--birhaus-spacing-v3-xs'] = theme.spacingV3.xs
    v3CSS['--birhaus-spacing-v3-sm'] = theme.spacingV3.sm
    v3CSS['--birhaus-spacing-v3-md'] = theme.spacingV3.md
    v3CSS['--birhaus-spacing-v3-lg'] = theme.spacingV3.lg
    v3CSS['--birhaus-spacing-v3-xl'] = theme.spacingV3.xl
    v3CSS['--birhaus-spacing-v3-2xl'] = theme.spacingV3['2xl']
    v3CSS['--birhaus-spacing-v3-3xl'] = theme.spacingV3['3xl']
    v3CSS['--birhaus-card-padding-v3'] = theme.spacingV3.cardPadding
    v3CSS['--birhaus-button-padding-v3'] = theme.spacingV3.buttonPadding
  }

  // v3 Radical border radius
  if (theme.borderRadiusV3) {
    v3CSS['--birhaus-radius-v3-xs'] = theme.borderRadiusV3.xs
    v3CSS['--birhaus-radius-v3-sm'] = theme.borderRadiusV3.sm
    v3CSS['--birhaus-radius-v3-md'] = theme.borderRadiusV3.md
    v3CSS['--birhaus-radius-v3-lg'] = theme.borderRadiusV3.lg
    v3CSS['--birhaus-radius-v3-xl'] = theme.borderRadiusV3.xl
    v3CSS['--birhaus-radius-v3-2xl'] = theme.borderRadiusV3['2xl']
    v3CSS['--birhaus-radius-v3-3xl'] = theme.borderRadiusV3['3xl']
    v3CSS['--birhaus-radius-card-v3'] = theme.borderRadiusV3.card
    v3CSS['--birhaus-radius-button-v3'] = theme.borderRadiusV3.button
  }

  // v3 Enhanced shadows
  if (theme.shadowV3) {
    v3CSS['--birhaus-shadow-v3-sm'] = theme.shadowV3.sm
    v3CSS['--birhaus-shadow-v3-md'] = theme.shadowV3.md
    v3CSS['--birhaus-shadow-v3-lg'] = theme.shadowV3.lg
    v3CSS['--birhaus-shadow-v3-xl'] = theme.shadowV3.xl
    v3CSS['--birhaus-shadow-glass'] = theme.shadowV3.glass
    v3CSS['--birhaus-shadow-floating'] = theme.shadowV3.floating
    v3CSS['--birhaus-shadow-elevated'] = theme.shadowV3.elevated
  }

  // v3 Animation properties
  if (theme.animationV3) {
    v3CSS['--birhaus-duration-fast'] = theme.animationV3.duration.fast
    v3CSS['--birhaus-duration-base'] = theme.animationV3.duration.base
    v3CSS['--birhaus-duration-slow'] = theme.animationV3.duration.slow
    v3CSS['--birhaus-easing-smooth'] = theme.animationV3.easing.smooth
    v3CSS['--birhaus-easing-snappy'] = theme.animationV3.easing.snappy
    v3CSS['--birhaus-easing-bounce'] = theme.animationV3.easing.bounce
    v3CSS['--birhaus-easing-gentle'] = theme.animationV3.easing.gentle
  }

  return { ...baseCSS, ...v3CSS }
}

// Spanish-first label helper
export function getBirhausLabel(
  key: string, 
  themeName: keyof BirhausThemeRegistry = 'financial',
  language: 'es' | 'en' = 'es'
) {
  const theme = getBirhausTheme(themeName)
  // Fix object injection by using explicit property access
  const labels = language === 'es' ? theme.labels.es : theme.labels.en
  // eslint-disable-next-line security/detect-object-injection
  return (labels as Record<string, string>)[key] || key
}

// Industry-specific theme recommendations
export const themeRecommendations = {
  'church-management': 'financial',
  'healthcare-system': 'healthcare',
  'school-management': 'education',
  'donation-platform': 'financial',
  'medical-records': 'healthcare',
  'learning-platform': 'education',
  'financial-reporting': 'financial',
  'patient-portal': 'healthcare',
  'student-portal': 'education'
} as const

export function getRecommendedTheme(useCase: keyof typeof themeRecommendations) {
  // Fix object injection by using explicit property access
  let themeName: keyof BirhausThemeRegistry
  switch (useCase) {
    case 'church-management':
    case 'donation-platform':
    case 'financial-reporting':
      themeName = 'financial'
      break
    case 'healthcare-system':
    case 'medical-records':
    case 'patient-portal':
      themeName = 'healthcare'
      break
    case 'school-management':
    case 'learning-platform':
    case 'student-portal':
      themeName = 'education'
      break
    default:
      themeName = 'financial'
  }
  return getBirhausTheme(themeName)
}

// v3 utilities are already exported where defined

// Create v3-specific Tailwind config for common use case
export function createTailwindV3Config(themeName: keyof BirhausThemeRegistry = 'financial') {
  return generateTailwindConfigV3(themeName)
}

// Export all themes as default
export default {
  themes: birhausThemes,
  utilities: {
    getBirhausTheme,
    getBirhausThemeCSS,
    getBirhausThemeCSSV3,
    useBirhausTheme,
    useBirhausThemeV3,
    generateTailwindConfig,
    generateTailwindConfigV3,
    createTailwindV3Config,
    generateCSSUtilities,
    generateCSSUtilitiesV3,
    generateGlassCSS,
    getBirhausLabel,
    getRecommendedTheme
  },
  registry: birhausThemes,
  v3: {
    themes: birhausThemes,
    useBirhausThemeV3,
    getBirhausThemeCSSV3,
    generateTailwindConfigV3,
    generateCSSUtilitiesV3,
    generateGlassCSS,
    createTailwindV3Config
  }
}